// src/Payment.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaMoon, FaSun, FaSpinner } from 'react-icons/fa';
import DiscountStrip from './component/DiscountStrip';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

function toman(n) {
  return `${Number(n || 0).toLocaleString('fa-IR')} تومان`;
}

export default function Payment() {
  const navigate = useNavigate();
  const location = useLocation();

  const state = location?.state || {};

  const planRequestId =
    state?.planRequestId ??
    state?.planId ??
    localStorage.getItem('planId') ??
    null;

  const title =
    state?.planTitle ??
    localStorage.getItem('planTitle') ??
    'برنامه';

  const basePriceToman =
    state?.finalPrice ??
    Number(localStorage.getItem('price') || 0);

  const campaignId = state?.campaignId ?? null;
  const campaignKey = state?.campaignKey ?? null;

  const [dark, setDark] = useState(true);
  const [discountCode, setDiscountCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  const [error, setError] = useState('');
  const [resolved, setResolved] = useState(null);

  const colors = useMemo(() => {
    if (dark) {
      return {
        bg: '#070B14',
        bg2: '#08101C',
        bg3: '#0A1224',
        card: 'rgba(21,29,51,0.96)',
        card2: 'rgba(14,20,37,0.96)',
        border: 'rgba(255,255,255,0.08)',
        text: '#FFFFFF',
        sub: '#B7C0D1',
        muted: '#8FA0BA',
        inputBg: '#10182A',
        headerBg: 'rgba(10,15,30,0.80)',
        danger: '#ef4444',
        ok: '#10b981',
      };
    }

    return {
      bg: '#F8FAFF',
      bg2: '#F2F6FF',
      bg3: '#EDF2FF',
      card: 'rgba(255,255,255,0.96)',
      card2: 'rgba(245,248,255,0.96)',
      border: 'rgba(15,23,42,0.10)',
      text: '#0F172A',
      sub: '#475569',
      muted: '#64748B',
      inputBg: '#FFFFFF',
      headerBg: 'rgba(255,255,255,0.82)',
      danger: '#ef4444',
      ok: '#10b981',
    };
  }, [dark]);

  const discountAmount = Number(
    resolved?.Result?.DiscountAmount ??
      resolved?.result?.discountAmount ??
      resolved?.discountAmount ??
      0
  );

  const resolvedOriginalAmount = Number(
    resolved?.Result?.OriginalAmount ??
      resolved?.result?.originalAmount ??
      resolved?.originalAmount ??
      0
  );

  const finalPrice =
    discountAmount > 0 && resolvedOriginalAmount > 0
      ? resolvedOriginalAmount - discountAmount
      : basePriceToman;

  useEffect(() => {
    if (!planRequestId || !basePriceToman) {
      alert('اطلاعات پرداخت ناقص است. لطفاً دوباره تلاش کنید.');
      navigate('/plans');
      return;
    }

    localStorage.setItem('planId', String(planRequestId));
    localStorage.setItem('planTitle', title);
    localStorage.setItem('price', String(basePriceToman));
  }, [planRequestId, basePriceToman, title, navigate]);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;

    try {
      setCheckingCode(true);
      setError('');
      setResolved(null);

      const token = localStorage.getItem('token');

      if (!token) {
        localStorage.setItem(
          'redirectAfterLogin',
          JSON.stringify({
            to: '/payment',
            state,
          })
        );
        navigate('/login');
        return;
      }

      const res = await fetch(`${API_URL}/api/payment/calculate-discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
body: JSON.stringify({
  PlanId: planRequestId,
  DiscountCode: discountCode.trim(),
  CampaignId: campaignId,
  CampaignKey: campaignKey,
  ReferralSource: 1,
}),
      });

      const data = await res.json();

      const amount =
        data?.Result?.DiscountAmount ??
        data?.result?.discountAmount ??
        data?.discountAmount ??
        0;

      if (res.ok && Number(amount) >= 0) {
        setResolved(data);
        return;
      }

setError(data?.Message || data?.message || 'کد وارد شده معتبر نیست.');
    } catch {
      setError('خطا در ارتباط با سرور.');
    } finally {
      setCheckingCode(false);
    }
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');

      if (!token) {
        localStorage.setItem(
          'redirectAfterLogin',
          JSON.stringify({
            to: '/payment',
            state,
          })
        );
        navigate('/login');
        return;
      }

      const res = await fetch(`${API_URL}/api/payment/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
body: JSON.stringify({
  planId: planRequestId,
  discountCode: discountCode?.trim() || null,
  campaignId,
  campaignKey,
  referralSource: 1,
}),
      });

      const data = await res.json();

      const redirectUrl =
        data?.redirectUrl ??
        data?.RedirectUrl ??
        data?.result?.redirectUrl ??
        data?.Result?.RedirectUrl;

      if (res.ok && redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      setError(data?.Message || data?.message || 'خطا در شروع پرداخت.');
    } catch {
      setError('مشکل در ارتباط با سرور پرداخت.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg2}, ${colors.bg3})`,
          color: colors.text,
        }}
      >
        <DiscountStrip />

        <header className="relative z-20 max-w-5xl mx-auto px-4 pt-4">
          <div
            className="min-h-[64px] rounded-3xl border px-4 flex items-center justify-between backdrop-blur-xl shadow-lg"
            style={{
              backgroundColor: colors.headerBg,
              borderColor: colors.border,
            }}
          >
            <div>
              <div className="font-black text-lg">پرداخت</div>
              <div className="text-xs mt-1" style={{ color: colors.muted }}>
                پرداخت مستقیم از طریق درگاه
              </div>
            </div>

            <button
              type="button"
              onClick={() => setDark((v) => !v)}
              className="w-11 h-11 rounded-2xl border flex items-center justify-center"
              style={{
                backgroundColor: colors.inputBg,
                borderColor: colors.border,
                color: colors.text,
              }}
            >
              {dark ? <FaSun /> : <FaMoon />}
            </button>
          </div>
        </header>

        <main className="relative z-10 max-w-5xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-[1fr_0.8fr] gap-5 items-start">
            <section
              className="rounded-[28px] border p-5 md:p-6 shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
                borderColor: colors.border,
              }}
            >
              <h1 className="text-2xl font-black mb-5">خلاصه سفارش</h1>

              <SummaryRow label="عنوان برنامه" value={title} colors={colors} />
              <SummaryRow label="قیمت اولیه" value={toman(basePriceToman)} colors={colors} />

              {discountAmount > 0 && (
                <>
                  <SummaryRow
                    label="مبلغ تخفیف"
                    value={toman(discountAmount)}
                    colors={{ ...colors, text: colors.ok }}
                  />
                  <SummaryRow
                    label="قیمت نهایی"
                    value={toman(finalPrice)}
                    colors={colors}
                    strong
                  />
                </>
              )}

              {discountAmount <= 0 && (
                <SummaryRow
                  label="قیمت قابل پرداخت"
                  value={toman(finalPrice)}
                  colors={colors}
                  strong
                />
              )}

              {resolved?.Message || resolved?.message ? (
                <p className="mt-4 text-sm leading-7" style={{ color: colors.sub }}>
                  {resolved?.Message || resolved?.message}
                </p>
              ) : null}
            </section>

            <aside
              className="rounded-[28px] border p-5 md:p-6 shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
                borderColor: colors.border,
              }}
            >
<h2 className="text-xl font-black mb-3">
  کد دوست / باشگاه / تخفیف
</h2>

<p className="text-sm leading-7 mb-4" style={{ color: colors.sub }}>
  اگر از دوستت، باشگاه یا موجیم کدی داری، اینجا وارد کن تا تخفیف اعمال شود.
</p>

              <input
                value={discountCode}
                onChange={(e) => {
                  setDiscountCode(e.target.value);
                  setResolved(null);
                  setError('');
                }}
                placeholder="کد را وارد کنید"
                className="w-full rounded-2xl border px-4 py-4 outline-none text-right mb-3"
                style={{
                  backgroundColor: colors.inputBg,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              />

<button
  type="button"
  disabled={checkingCode || !discountCode.trim()}
  onClick={applyDiscount}
  className="
    group
    relative
    w-full
    h-14
    overflow-hidden
    rounded-2xl
    bg-gradient-to-l
    from-blue-600
    via-indigo-600
    to-purple-600
    px-5
    text-white
    font-black
    shadow-lg
    shadow-purple-500/25
    transition-all
    duration-300
    hover:scale-[1.01]
    hover:shadow-purple-500/40
    active:scale-[0.98]
    disabled:opacity-60
    disabled:hover:scale-100
    disabled:shadow-none
  "
>
  {/* glow */}
  <div
    className="
      absolute
      inset-0
      opacity-0
      group-hover:opacity-100
      transition
      duration-500
      bg-gradient-to-r
      from-transparent
      via-white/15
      to-transparent
      -translate-x-full
      group-hover:translate-x-full
    "
  />

  <div className="relative flex items-center justify-center gap-3">
    {checkingCode ? (
      <>
        <div className="relative">
          <FaSpinner
            size={18}
            className="animate-spin"
          />

          <span className="absolute inset-0 animate-ping rounded-full bg-white/30" />
        </div>

        <div className="flex flex-col items-start">
          <span className="text-sm font-black">
            در حال بررسی...
          </span>

          <span className="text-[10px] text-white/70">
            چند لحظه صبر کن
          </span>
        </div>
      </>
    ) : (
      <>
        <span className="text-xl">🎁</span>

        <div className="flex flex-col items-start">
          <span className="text-sm font-black">
            اعمال کد تخفیف
          </span>

          <span className="text-[10px] text-white/70">
            بررسی و محاسبه قیمت نهایی
          </span>
        </div>
      </>
    )}
  </div>
</button>

              {discountAmount > 0 && (
                <div
                  className="mt-4 rounded-2xl border p-4 text-sm leading-7"
                  style={{
                    borderColor: 'rgba(16,185,129,0.25)',
                    backgroundColor: dark
                      ? 'rgba(6,78,59,0.22)'
                      : 'rgba(236,253,245,0.96)',
                    color: colors.ok,
                  }}
                >
                  تخفیف با موفقیت اعمال شد.
                  <div className="font-black mt-1">
                    قیمت نهایی: {toman(finalPrice)}
                  </div>
                </div>
              )}

              {error && (
                <div
                  className="mt-4 rounded-2xl border p-4 text-sm text-center font-bold"
                  style={{
                    borderColor: 'rgba(239,68,68,0.25)',
                    backgroundColor: dark
                      ? 'rgba(127,29,29,0.20)'
                      : 'rgba(254,242,242,0.96)',
                    color: colors.danger,
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="button"
                disabled={loading || !planRequestId}
                onClick={handlePayment}
                className="mt-5 w-full min-h-[58px] rounded-2xl bg-gradient-to-l from-green-600 to-emerald-500 text-white font-black disabled:opacity-60 flex flex-col items-center justify-center"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <FaSpinner className="animate-spin" />
                    در حال انتقال...
                  </span>
                ) : (
                  <>
                    پرداخت نهایی
                    <span className="text-xs font-normal mt-1 opacity-90">
                      مبلغ قابل پرداخت: {toman(finalPrice)}
                    </span>
                  </>
                )}
              </button>
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, colors, strong = false }) {
  return (
    <div
      className="flex items-center justify-between gap-4 py-3 border-b last:border-b-0"
      style={{ borderColor: colors.border }}
    >
      <div className="text-sm" style={{ color: colors.sub }}>
        {label}
      </div>

      <div
        className={strong ? 'font-black text-lg' : 'font-bold text-sm'}
        style={{ color: colors.text }}
      >
        {value || '-'}
      </div>
    </div>
  );
}